import { CfFlowResponse, CfFlowScreen } from '../api/cloudflareFlowApi';
import { isFailedStatus } from '../cfNexusOpening';
import { CfVerificationRetry } from '../components/cfVerificationRetry';
import { CfNexusHandoff, buildNexusSummary } from './buildNexusSummary';
import { deploymentFailureRowFor } from './deploymentFailureRow';
import { preflightFailureRowFor } from './preflightFailureRow';
import { VERIFICATION_HELP_PROMPT } from './verificationHelpPrompt';

/**
 * Which handoff the error card is. It cannot be read off the status alone: a
 * `deploy_failed` flow whose rollback also failed is a different conversation
 * from one that reverted cleanly.
 */
const handoffFor = (flow: CfFlowResponse): CfNexusHandoff => {
  if (flow.rollbackOutcome === 'failed') return 'rollback_failed';
  if (flow.status === 'preflight_failed') return 'preflight_failure';

  return 'deployment_failure';
};

function errorCardPrompt(flow: CfFlowResponse, knownSecrets: readonly string[]): string {
  const handoff = handoffFor(flow);
  const { step, error } =
    handoff === 'preflight_failure' ? preflightFailureRowFor(flow) : deploymentFailureRowFor(flow);

  return buildNexusSummary({
    handoff,
    flow,
    step,
    errorMessage: error,
    leftovers: flow.errorDetail?.leftovers ?? [],
    knownSecrets,
  });
}

export type CfScreenNexusPromptInput = {
  flow: CfFlowResponse;
  screen: CfFlowScreen | null;
  verificationRetry: CfVerificationRetry;
  knownSecrets: readonly string[];
};

/** The prefill of the Ask Nexus button the screen shows, or `undefined` when it shows none. */
export function screenNexusPrompt({
  flow,
  screen,
  verificationRetry,
  knownSecrets,
}: CfScreenNexusPromptInput): string | undefined {
  if (screen !== 'error' || !isFailedStatus(flow.status)) return undefined;

  if (flow.status === 'verification_failed') {
    return verificationRetry === 'none' ? VERIFICATION_HELP_PROMPT : undefined;
  }

  return errorCardPrompt(flow, knownSecrets);
}
